<template>
    <div class="AftercareCart">
        <div class="AftercareCart--title">
            <div class="AftercareCart--title-name"
                 :title="aftercare.aftercareName">
                {{aftercare.aftercareName}}
            </div>
            <div class="AftercareCart--title-date">
                {{aftercare.aftercareStartDate | timeFormatL}} - {{aftercare.aftercareEndDate | timeFormatL}}
            </div>
        </div>

        <div class="AftercareCart--second">
            <div class="AftercareCart--second-location">
                <img src="../../../../assets/img/svg/map-pin.svg">
                <span class="AftercareCart--second-location-name">{{aftercare.locationName }}</span>
            </div>
            <div>
                <div class="d-flex align-items-center">
                    <strong :style="{'margin-right': '10px'}">Sessions Fees:</strong>
                    <span v-if="aftercare.price === 0"
                          class="font-weight-bold text-green">
                        FREE
                    </span>
                    <span v-else
                          class="AftercareCart--second-price">
                        {{schoolCurrency | currencySign}} {{ aftercare.price | priceFormat}}
                        <span v-if="aftercare.billingRate !== 'FIXED'"> / {{aftercare.billingRate | toCapitalize}}</span>
                    </span>
                </div>
                <div v-if="aftercare.dropIn"
                     class="d-flex align-items-center">
                    <strong :style="{'margin-right': '15px'}">Drop-in Fees:</strong>
                    <span v-if="aftercare.dropInPrice === 0"
                          class="font-weight-bold text-green">
                        FREE
                    </span>
                    <span v-else
                          class="AftercareCart--second-price">
                        {{schoolCurrency | currencySign}} {{ aftercare.dropInPrice | priceFormat}}
                        <span v-if="aftercare.dropInBillingRate !== 'FIXED'">/ {{aftercare.dropInBillingRate | toCapitalize}}</span>
                    </span>
                </div>
            </div>
        </div>

        <div class="AftercareCart--content">
            <div class="AftercareCart--content-info">
                <p :class="isOpened ? 'AftercareCart--content-info-text-full' : 'AftercareCart--content-info-text-small'"
                   v-html="aftercare.description"
                />
                <transition name="fade">
                    <div v-if="!isOpened"
                         class="description-shadow"
                    />
                </transition>
            </div>
            <div class="AftercareCart--content-more"
                 @click="toggleCart">
                <i class="material-icons down" :class="isOpened ? 'rotated': 'rotatedBack'">keyboard_arrow_down</i>
            </div>
            <div class="AftercareCart--content-table">
                <div class="AftercareCart--content-table-header">
                    <div class="AftercareCart--content-table-header-info">
                        <h6 class="AftercareCart--content-table-header-title">Days</h6>
                        <h6 class="AftercareCart--content-table-header-title">
                            <span v-if="schoolCountry === 'England'">Age/Year</span>
                            <span v-else>Age/Grades</span>
                        </h6>
                        <h6 class="AftercareCart--content-table-header-title">Time</h6>
                        <h6 class="AftercareCart--content-table-header-title">Max</h6>
                    </div>
                </div>
                <div v-for="day of aftercare.days"
                     :key="day.id"
                     class="AftercareCart--content-table-day">
                    <aftercare-day-cart :session="day"
                                        :aftercare="aftercare"
                                        @open="$emit('open', day)"
                    />
                </div>
                <template v-if="aftercare.dropIn">
                    <div class="AftercareCart--content-table-footer">
                        <div class="AftercareCart--content-table-footer-title">DROP-IN</div>
                        <div class="AftercareCart--content-table-footer-button">
                            <button class="primary-btn"
                                    :disabled="aftercare.dropInStatus === 'Closed'"
                                    @click.prevent="$emit('drop-in')">
                                <template v-if="aftercare.dropInStatus === 'Online'"> Add </template>
                                <template v-else-if="aftercare.dropInStatus === 'Closed'"> Closed </template>
                            </button>
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapGetters } from 'vuex';
    import { ExpandAnimationMixin } from '@/mixins/expandAnimationMixin';

    export default {
        name: 'AftercareCart',

        components: {
            AftercareDayCart: () => import('@/components/dashboard/pages/Activities/AftercareDayCart')
        },

        props: {
            aftercare: {
                type: Object,
                required: true
            }
        },

        data() {
            return {
                isOpened: false
            }
        },

        computed: {
            ...mapGetters(['schoolCurrency', 'schoolCountry'])
        },

        mixins: [ExpandAnimationMixin],

        methods: {
            toggleCart() {
                this.isOpened = !this.isOpened;
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../../../assets/styles/variables/colors';

    .AftercareCart {
        width: 100%;
        padding: 28px 30px 25px;
        border-radius: 4px;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.15);
        background-color: $White;

        &--title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            flex-wrap: wrap;
            margin-bottom: 17px;
            @media screen and (max-width: 700px) {
                flex-direction: column;
                justify-content: space-between;
                margin-bottom: 10px;
            }

            &-name {
                margin-bottom: 0;
                cursor: default;
                font-size: 24px;
                font-weight: 700;
                line-height: 1.33;
                word-break: break-word;
                color: $Dark;
                @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) { //for IE
                    word-break: break-all;
                }
                @media screen and (max-width: 700px) {
                    text-align: center;
                }
            }

            &-date {
                font-size: 14px;
                font-weight: 400;
                color: $WhiteGrey;
                @media screen and (max-width: 700px) {
                    margin-top: 10px;
                }
            }
        }

        &--second {
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 15px;
            @media screen and (max-width: 700px) {
                flex-direction: column;
                justify-content: space-between;
            }

            &-location {
                img {
                    height: 10px;
                    width: 13px;
                }

                &-name {
                    text-transform: capitalize;
                    font-weight: 400;
                    font-size: 14px;
                    word-break: break-word;
                    color: $Light-dark;
                    @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) { //for IE
                        word-break: break-all;
                    }
                }
            }

            &-price {
                font-size: 16px;
                font-weight: 700;
                line-height: 2;
                color: $SoftBlue;

                @media screen and (max-width: 700px) {
                    width: 100%;
                }
            }
        }

        &--content {
            &-info {
                position: relative;
                font-size: 15px;
                font-weight: 400;
                line-height: 1.6;
                color: $Dark;
                word-break: break-word;
                @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) { //for IE
                    word-break: break-all;
                }

                .description-shadow {
                    position: absolute;
                    bottom: 0;
                    width: 100%;
                    height: 49px;
                    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(255, 255, 255, 0.54) 54%, #ffffff)
                }

                &-text-small {
                    height: 60px;
                    overflow: hidden;
                }

                &-text-full {
                    animation: accordion 15s forwards;
                }
            }

            &-more {
                width: 40px;
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                margin: 0 auto;
                border-radius: 50%;
                transform: rotate(180deg);
                box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.2);
                cursor: pointer;
                background-color: $White;

                i {
                    position: relative;
                    display: block;
                    font-size: 28px;
                    color: $Blue;
                }
            }

            &-table {
                &-header {
                    width: 100%;
                    padding: 15px 0;
                    border-bottom: 1px solid $ComplementaryBgTxtDarker;
                    @media screen and (max-width: 700px) {
                        display: none;
                    }

                    &-info {
                        width: 80%;
                        display: flex;
                    }

                    &-title {
                        width: 25%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 0;
                        font-size: 16px;
                        font-weight: 700;
                        line-height: 1.71;
                        color: $Dark;
                        &:first-child {
                            justify-content: start;
                        }
                    }
                }
                &-day {
                    border-bottom: 1px solid $ComplementaryBgTxtDarker;
                    &:last-child {
                        border-bottom: none;
                    }
                }

                &-footer {
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 15px 0;

                    &-title {
                        width: 80%;
                        margin-bottom: 0;
                        font-size: 14px;
                        font-weight: 500;
                        line-height: 1.71;
                        color: $Dark;
                        @media screen and (max-width: 700px) {
                            width: 70%;
                        }
                    }

                    &-button {
                        width: 20%;
                        display: flex;
                        justify-content: flex-end;
                        @media screen and (max-width: 700px) {
                            width: 30%;
                        }

                        button {
                            width: 99px;
                            height: 32px;
                        }
                    }
                }
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/dashboard/pages/Activities/AftercareCart.vue